import { cn } from '@/lib/utils';

import { ChannelBadge } from './ChannelBadge';

import type { MessagingConversation } from '@/lib/api/messaging.api';

const formatRelativeTime = (dateStr: string | null) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);

  if (diffMins < 1) return 'now';
  if (diffMins < 60) return `${diffMins}m`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d`;
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
};

interface ConversationItemProps {
  conversation: MessagingConversation;
  isSelected: boolean;
  onClick: () => void;
}

export const ConversationItem = ({ conversation, isSelected, onClick }: ConversationItemProps) => {
  const displayName =
    conversation.contact?.name ||
    (conversation.channel === 'EMAIL'
      ? conversation.remote_address
      : conversation.contact?.phone || conversation.remote_address);
  const hasUnread = conversation.unread_count > 0;

  return (
    <button
      onClick={onClick}
      className={cn(
        'flex w-full items-start gap-3 rounded-lg px-3 py-3 text-left transition-colors',
        isSelected ? 'bg-accent text-accent-foreground' : 'hover:bg-accent/50',
      )}
    >
      <div className="bg-primary/10 flex h-10 w-10 shrink-0 items-center justify-center rounded-full">
        <span className="text-primary text-sm font-semibold">
          {(
            conversation.contact?.name?.[0] ??
            conversation.remote_address?.[0] ??
            '?'
          ).toUpperCase()}
        </span>
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <span className={cn('truncate text-sm', hasUnread ? 'font-semibold' : 'font-medium')}>
            {displayName}
          </span>
          <span className="text-muted-foreground shrink-0 text-xs">
            {formatRelativeTime(conversation.last_message_at)}
          </span>
        </div>

        <div className="flex items-center justify-between gap-2">
          <p
            className={cn(
              'truncate text-xs',
              hasUnread ? 'text-foreground font-medium' : 'text-muted-foreground',
            )}
          >
            {conversation.last_message_preview ?? 'No messages yet'}
          </p>
          <div className="flex shrink-0 items-center gap-1.5">
            {hasUnread && (
              <span className="bg-primary text-primary-foreground flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-[10px] font-bold">
                {conversation.unread_count > 99 ? '99+' : conversation.unread_count}
              </span>
            )}
            <ChannelBadge channel={conversation.channel} />
          </div>
        </div>
      </div>
    </button>
  );
};
